import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useLocation} from 'react-router-dom'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {ROUTES} from '../../../../_gori/constants'
import {
  DEFAULT_BRANCHES_MENU,
  getSelectedAccountIds,
  mapBranchesResponseToMenuData,
} from '../components/BranchesMenu'
import type {BranchesMenuData} from '../core/_types'
import OrganizationService from '../core/_requests'
import {useAuth} from '../../auth/core/Auth'
import {isEnterprise} from '../../auth'

const ASIDE_DEBOUNCE_MS = 1000

const hasSelectionChanged = (previousMenu: BranchesMenuData, nextMenu: BranchesMenuData) =>
  !isEqual(getSelectedAccountIds(previousMenu), getSelectedAccountIds(nextMenu))

type OrganizationBranchesContextModel = {
  menuData: BranchesMenuData
  appliedAccountIds: Array<string | number>
  isBranchesReady: boolean
  setMenuDataFromAside: (menuData: BranchesMenuData) => void
  applyMenuDataFromModal: (menuData: BranchesMenuData) => void
}

const OrganizationBranchesContext = React.createContext<
  OrganizationBranchesContextModel | undefined
>(undefined)

const OrganizationBranchesProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const {newCancelToken, isCancel} = useCancelToken()
  const {pathname} = useLocation()
  const isOrganizationsPage =
    pathname === ROUTES.ORGANIZATIONS.BASE || pathname.startsWith(`${ROUTES.ORGANIZATIONS.BASE}/`)
  const [menuData, setMenuData] = useState<BranchesMenuData>(DEFAULT_BRANCHES_MENU)
  const [appliedAccountIds, setAppliedAccountIds] = useState<Array<string | number>>(() =>
    getSelectedAccountIds(DEFAULT_BRANCHES_MENU)
  )
  const [isBranchesReady, setIsBranchesReady] = useState(false)
  const {currentUser} = useAuth()
  const menuDataRef = useRef(menuData)
  const hasUserChangedSelectionRef = useRef(false)

  menuDataRef.current = menuData

  useEffect(() => {
    hasUserChangedSelectionRef.current = false
  }, [currentUser?.id])

  const syncAppliedAccountIds = useCallback((nextMenu: BranchesMenuData) => {
    const nextAccountIds = getSelectedAccountIds(nextMenu)
    setAppliedAccountIds((current) => (isEqual(current, nextAccountIds) ? current : nextAccountIds))
  }, [])

  const applyAccountIdsRef = useRef(syncAppliedAccountIds)
  applyAccountIdsRef.current = syncAppliedAccountIds

  const debouncedApplyAside = useMemo(
    () => debounce(() => applyAccountIdsRef.current(menuDataRef.current), ASIDE_DEBOUNCE_MS),
    []
  )

  useEffect(() => () => debouncedApplyAside.cancel(), [debouncedApplyAside])

  const setMenuDataFromAside = useCallback(
    (nextMenu: BranchesMenuData) => {
      const selectionChanged = hasSelectionChanged(menuDataRef.current, nextMenu)

      menuDataRef.current = nextMenu
      setMenuData(nextMenu)

      if (selectionChanged) {
        hasUserChangedSelectionRef.current = true
        debouncedApplyAside()
      }
    },
    [debouncedApplyAside]
  )

  const applyMenuDataFromModal = useCallback(
    (nextMenu: BranchesMenuData) => {
      debouncedApplyAside.cancel()
      hasUserChangedSelectionRef.current = true

      menuDataRef.current = nextMenu
      setMenuData(nextMenu)
      syncAppliedAccountIds(nextMenu)
    },
    [debouncedApplyAside, syncAppliedAccountIds]
  )

  useEffect(() => {
    if (!isOrganizationsPage || !currentUser || !isEnterprise(currentUser)) {
      setIsBranchesReady(true)
      return
    }

    let cancelled = false
    setIsBranchesReady(false)

    const loadAccountBranches = async () => {
      try {
        const response = await OrganizationService.getAccountBranches({
          cancelToken: newCancelToken(),
        })

        if (cancelled || hasUserChangedSelectionRef.current) {
          return
        }

        const nextMenu = mapBranchesResponseToMenuData(response)
        if (!nextMenu.branches.length && !nextMenu.unassignedAccounts.length) {
          return
        }

        menuDataRef.current = nextMenu
        setMenuData(nextMenu)
        syncAppliedAccountIds(nextMenu)
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        if (!cancelled) {
          setIsBranchesReady(true)
        }
      }
    }

    loadAccountBranches()

    return () => {
      cancelled = true
    }
  }, [isOrganizationsPage, currentUser, isCancel, newCancelToken, syncAppliedAccountIds])

  const contextValue = useMemo(
    () => ({
      menuData,
      appliedAccountIds,
      isBranchesReady,
      setMenuDataFromAside,
      applyMenuDataFromModal,
    }),
    [menuData, appliedAccountIds, isBranchesReady, setMenuDataFromAside, applyMenuDataFromModal]
  )

  return (
    <OrganizationBranchesContext.Provider value={contextValue}>
      {children}
    </OrganizationBranchesContext.Provider>
  )
}

const useOrganizationBranches = () => {
  const context = useContext(OrganizationBranchesContext)
  if (!context) {
    throw new Error('useOrganizationBranches must be used within OrganizationBranchesProvider')
  }
  return context
}

export {OrganizationBranchesProvider, useOrganizationBranches}
